/**
 * 쿠키 기반 토큰 유틸리티
 *
 * server/client 환경 모두 동작 (no 'use client' directive)
 * shared 레이어: config, workspace-pkg만 import 가능
 */

export type AuthRole = "buyer" | "admin";

const COOKIE_NAMES: Record<AuthRole, string> = {
  buyer: "token",
  admin: "admin_token",
};

/** Bearer prefix 제거 */
function stripBearer(value: string): string {
  return value.startsWith("Bearer ") ? value.slice(7) : value;
}

/** 쿠키 문자열에서 특정 키 값 파싱 */
function parseCookieValue(cookieStr: string, name: string): string | null {
  const match = cookieStr
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  if (!match) return null;
  const raw = decodeURIComponent(match.slice(name.length + 1));
  return stripBearer(raw);
}

/**
 * 토큰 조회
 * - client: document.cookie 파싱
 * - server: next/headers cookies() dynamic import
 */
export async function getToken(role: AuthRole): Promise<string | null> {
  const name = COOKIE_NAMES[role];

  if (typeof window === "undefined") {
    // server-side
    try {
      const { cookies } = await import("next/headers");
      const store = await cookies();
      const raw = store.get(name)?.value ?? null;
      return raw ? stripBearer(raw) : null;
    } catch {
      return null;
    }
  }

  // client-side
  return parseCookieValue(document.cookie, name);
}

/**
 * 토큰 설정 (client-side only)
 * secure=true, sameSite=Lax, path=/
 */
export function setToken(role: AuthRole, value: string): void {
  if (typeof window === "undefined") return;

  // 기존 중복 쿠키 제거 후 새로 설정
  removeToken(role);

  const name = COOKIE_NAMES[role];
  const encoded = encodeURIComponent(value);
  const isSecure = window.location.protocol === "https:";
  document.cookie = `${name}=${encoded}; path=/; sameSite=Lax${isSecure ? "; secure" : ""}`;
}

/**
 * 토큰 삭제 (client-side only)
 * 속성 조합이 다른 중복 쿠키를 모두 제거하기 위해 여러 변형으로 삭제
 */
export function removeToken(role: AuthRole): void {
  if (typeof window === "undefined") return;

  const name = COOKIE_NAMES[role];
  const expire = "max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT";

  const paths = role === "admin" ? ["/", "/admin", "/admin/login"] : ["/"];

  for (const p of paths) {
    document.cookie = `${name}=; path=${p}; ${expire}`;
    document.cookie = `${name}=; path=${p}; ${expire}; secure`;
    document.cookie = `${name}=; path=${p}; ${expire}; sameSite=Lax`;
    document.cookie = `${name}=; path=${p}; ${expire}; sameSite=Lax; secure`;
  }
}

/**
 * 토큰 존재 여부 확인 (동기, proxy 등에서 사용)
 * - client: document.cookie 파싱
 * - server: 동기 호출 불가로 항상 false 반환 (getToken 사용 권장)
 */
export function hasToken(role: AuthRole): boolean {
  if (typeof window === "undefined") return false;

  const name = COOKIE_NAMES[role];
  return document.cookie.split(";").some((c) => c.trim().startsWith(`${name}=`));
}
